/**
 * Documents state management.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export const fetchDocuments = createAsyncThunk(
  'documents/fetch',
  async ({ folderId, page = 1 }, { rejectWithValue }) => {
    try {
      const params = { page, page_size: 50 };
      if (folderId) params.folder_id = folderId;
      
      const response = await api.get('/documents', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch documents');
    }
  }
);

export const uploadDocument = createAsyncThunk(
  'documents/upload',
  async ({ file, folderId }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (folderId) formData.append('folder_id', folderId);
      
      const response = await api.post('/documents/upload', formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Upload failed');
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'documents/delete',
  async (documentId, { rejectWithValue }) => {
    try {
      console.log('🔌 DELETE API call to:', `/documents/${documentId}`);
      const response = await api.delete(`/documents/${documentId}`);
      console.log('🔌 DELETE API response:', response);
      return documentId;
    } catch (error) {
      console.error('🔌 DELETE API error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Delete failed');
    }
  }
);

const documentsSlice = createSlice({
  name: 'documents',
  initialState: {
    items: [],
    total: 0,
    page: 1,
    loading: false,
    error: null,
    uploadProgress: 0,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.documents;
        state.total = action.payload.total;
        state.page = action.payload.page;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.items = state.items.filter(doc => doc.id !== action.payload);
        state.loading = false;
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const { clearError } = documentsSlice.actions;
export default documentsSlice.reducer;
